<template>
	<main class="mypage edit">
		<UserMenu v-if="displayStore.isDesktop" />
		<article class="userInfo">
			<section class="user_form">
				<h6>내 정보 변경</h6>

				<div class="c_fieldArea">
					<FieldLayout title="아이디">
						<InputText :value="myData?.member.member_id" max-length="30" readonly />
					</FieldLayout>
					<FieldLayout title="이름">
						<InputText :value="myData?.member.member_name" max-length="30" readonly />
					</FieldLayout>
					<FieldLayout
						title="닉네임"
						:success="nickname_valid ? successNickname : ''"
						:error="errors.nickname || errors.nickname_valid"
						@input="
							() => {
								if (nickname_valid) nickname_valid = false;
							}
						"
					>
						<InputText
							v-model="nickname"
							:show-error-icon="errors.nickname"
							:class="{ error: errors.nickname }"
							:error="errors.nickname"
						/>
						<button type="button" class="c_button radius-x-small primary-fill" @click="onClickNickname">확인</button>
					</FieldLayout>
					<FieldLayout title="휴대폰번호">
						<InputText v-model="phone" readonly />
						<button type="button" class="c_button primary-fill radius-x-small" @click="onClickChangePhoneForm">
							변경
						</button>
					</FieldLayout>
					<div class="receive">
						<span>서비스 알림 수신 설정</span>
						<SliderCheckbox value="" :model-value="isReceive" @update:model-value="onChangeSlider" />
					</div>
					<button class="c_button radius-x-small primary-fill save" @click="onClickSave">저장</button>
					<div class="button_wrap">
						<button class="c_button radius-x-small secession" @click="$router.push('/my/edit/account-delete')">
							회원탈퇴
						</button>
					</div>
				</div>
			</section>
		</article>
	</main>
</template>

<script setup lang="ts">
import { ref, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import { useCheckDuplicateNicknameApi } from '@/api/auth';
import { useModifyMemberInfoApi, useMypageGetMyDataApi } from '@/api/mypage';
import SliderCheckbox from '@/components/atom/checkbox/SliderCheckbox.vue';
import InputText from '@/components/atom/input/InputText.vue';
import FieldLayout from '@/components/form/FieldLayout.vue';
import UserMenu from '@/components/mypage/UserMenu.vue';
import { nicknameRegex } from '@/constants/regex';
import {
	ALREADY_NICKNAME,
	COMPANY_TITLE,
	ERROR_INVALID_INPUT_FORMAT,
	MESSAGE_AVAILABLE_NICKNAME,
	MESSAGE_NICKNAME_VERIFICATION_COMPLETED,
	SAVE_POST_COMPLETED,
} from '@/constants/title';
import { useDisplayStore } from '@/stores/display';
// store
import { useToastStore } from '@/stores/toast';

// -----variable-----
const router = useRouter();
const toastStore = useToastStore();
const displayStore = useDisplayStore();

const isOpenPhoneForm = ref(false);
const phone = ref('0102113123');
const isReceive = ref(true);
const successNickname = ref('사용 가능한 닉네임입니다.');

const today = dayjs().format('YYYY-MM-DD');

// -----validation-----
const { errors, defineField, setFieldError, validate } = useForm({
	validationSchema: yup.object({
		nickname: yup
			.string()
			.matches(nicknameRegex, ERROR_INVALID_INPUT_FORMAT)
			.min(2, ERROR_INVALID_INPUT_FORMAT)
			.max(8, ERROR_INVALID_INPUT_FORMAT),
		nickname_valid: yup.boolean().isTrue(MESSAGE_NICKNAME_VERIFICATION_COMPLETED),
	}),
	initialValues: { nickname: 'LULU122', nickname_valid: false },
});
const [nickname] = defineField('nickname');
const [nickname_valid] = defineField('nickname_valid');

// -----api-----
const { data: myData } = useMypageGetMyDataApi();
const { mutateAsync: checkDuplicateNickname } = useCheckDuplicateNicknameApi();
const { mutateAsync: modifyMemberInfo } = useModifyMemberInfoApi();

// -----function-----
const onChangeSlider = (boolean: boolean) => {
	isReceive.value = boolean;
	if (isReceive.value) {
		toastStore.openToast({
			subContent: displayStore.isDesktop ? COMPANY_TITLE : '',
			content: `서비스 알림 수신 동의 완료 (${today})`,
		});
	} else {
		toastStore.openToast({
			subContent: displayStore.isDesktop ? COMPANY_TITLE : '',
			content: `서비스 알림 수신 거부 완료 (${today})`,
		});
	}
};
const onClickNickname = async () => {
	const res = await checkDuplicateNickname({
		member_nickname: nickname.value,
	});
	if (res.data.success) {
		nickname_valid.value = true;
		successNickname.value = MESSAGE_AVAILABLE_NICKNAME;
	} else if (!res.data.success) {
		setFieldError('nickname', ALREADY_NICKNAME);
	}
};

const onClickChangePhoneForm = () => {
	isOpenPhoneForm.value = !isOpenPhoneForm.value;
	router.push({
		path: '/my/edit/phone',
	});
};
const onClickSave = async () => {
	const { valid } = await validate();
	if (!valid) {
		return;
	}
	const res = await modifyMemberInfo({
		member_nickname: nickname.value,
		member_push_agree: isReceive.value,
	});
	if (res.data.success) {
		toastStore.openToast({
			subContent: COMPANY_TITLE,
			content: SAVE_POST_COMPLETED,
		});
		successNickname.value = '';
	}
};

watchEffect(() => {
	nickname.value = myData.value?.member.member_nickname ?? '';
	phone.value = myData.value?.member.member_contact ?? '';
	isReceive.value = myData.value?.member.member_push_agree === 1;
});
</script>

<style lang="scss" scoped>
.edit {
	margin-bottom: 7.2rem;
	.userInfo {
		max-width: 40rem;
		margin: 0 auto;
		width: 100%;
		.user_form {
			display: flex;
			flex-direction: column;
			gap: var(--space-xxx-large);

			.form_phone {
				display: flex;
				flex-direction: column;
				gap: 1rem;
			}
			.save {
				height: 4.8rem;
			}
			.button_wrap {
				text-align: center;
				.secession {
					width: fit-content;
					border-bottom: 1px solid var(--color-base-light);
					border-radius: 0;
					padding: 0;
					height: 1.8rem;
					font-weight: var(--font-w-regular);
					font-size: var(--font-s-mid);
					color: var(--color-base-light);
					line-height: 1.9rem;
				}
			}
		}
	}
}
</style>
